import apiClient, { getCsrfCookie } from './client';
import type {
  SystemSettings,
  UpdateSettingsDTO,
  PaymentMethodsResponse,
  UpdatePaymentMethodsDTO,
  GatewaySettings,
  UpdateGatewaySettingsDTO,
  GatewayTestResponse,
  DocumensoSettings,
  UpdateDocumensoSettingsDTO,
  DocumensoTestResponse,
  BillingPlatformSettings,
  BillingPlatformTestResponse,
  FacturaDirectaCredentialsSettings,
  UpdateFacturaDirectaCredentialsDTO,
  FacturaDirectaCredentialsTestResponse,
  AnthropicSettings,
  UpdateAnthropicSettingsDTO,
  AnthropicTestResponse,
  UiSettings,
  UpdateUiSettingsDTO,
  SlaComplianceSettings,
  UpdateSlaComplianceSettingsDTO,
} from '../types/settings';
import type {
  PauseType,
  PauseTypeResponse,
  PauseTypesListResponse,
  CreatePauseTypeDTO,
  UpdatePauseTypeDTO,
} from '../types/pauseType';
import type {
  CompanySettings,
  CompanySettingsFormData,
  FileUploadResponse,
  CompanyFileType,
} from '../types/companySettings';
import type {
  EmailSettings,
  UpdateEmailSettingsDTO,
  MailProbeResponse,
  MailSendTestResponse,
  MailSendTestPayload,
  TicketSettings,
  UpdateTicketSettingsDTO,
  ContactDefaultsSettings,
  UpdateContactDefaultsSettingsDTO,
} from '../types/emailSettings';
import type { BrandingSettings, UpdateBrandingSettingsDTO } from '../types/branding';

// Base path for settings endpoints
const SETTINGS_BASE = '/api/admin/settings';

/**
 * Languages settings (P3 F9 — multi-language architecture).
 */
export interface LanguagesAvailableLocale {
  code: string;
  name: string;
  native_name: string;
}

export interface LanguagesSettings {
  available_locales: LanguagesAvailableLocale[];
  enabled_locales: string[];
  default_locale: string;
  fallback_chain: string[];
}

export interface UpdateLanguagesSettingsDTO {
  enabled_locales: string[];
  default_locale: string;
  fallback_chain?: string[];
}

/**
 * Public Branding type (for unauthenticated requests)
 */
export interface PublicBranding {
  app_name: string;
  logo_url: string | null;
  favicon_url: string | null;
  /** #404 — operator brand colour consumed by the customer portal. */
  brand_color?: string;
}

/**
 * Get public branding settings (no authentication required).
 * Used for login page and other unauthenticated pages.
 */
export async function getPublicBranding(): Promise<PublicBranding> {
  const response = await apiClient.get<{ data: PublicBranding }>('/api/branding');
  return response.data.data;
}

/**
 * Settings API Module
 * Handles all settings-related API calls
 */
export const settingsApi = {
  /**
   * Get all system settings
   */
  async getAll(): Promise<SystemSettings> {
    const response = await apiClient.get<{ data: SystemSettings }>(`${SETTINGS_BASE}`);
    return response.data.data;
  },

  /**
   * Update system settings
   */
  async update(data: UpdateSettingsDTO): Promise<SystemSettings> {
    await getCsrfCookie();
    const response = await apiClient.put<{ data: SystemSettings }>(`${SETTINGS_BASE}`, data);
    return response.data.data;
  },

  /**
   * Get a specific setting by key
   */
  async getByKey(key: string): Promise<unknown> {
    const response = await apiClient.get<{ data: unknown }>(`${SETTINGS_BASE}/${key}`);
    return response.data.data;
  },

  /**
   * Update a specific setting by key
   */
  async updateByKey(key: string, value: unknown): Promise<unknown> {
    await getCsrfCookie();
    const response = await apiClient.put<{ data: unknown }>(`${SETTINGS_BASE}/${key}`, { value });
    return response.data.data;
  },

  /**
   * Pause Types API
   */
  pauseTypes: {
    /**
     * Get all pause types
     */
    async list(): Promise<PauseType[]> {
      const response = await apiClient.get<PauseTypesListResponse>(
        `${SETTINGS_BASE}/pause-types`
      );
      return response.data.data;
    },

    /**
     * Get a single pause type by ID
     */
    async getById(id: number): Promise<PauseType> {
      const response = await apiClient.get<PauseTypeResponse>(
        `${SETTINGS_BASE}/pause-types/${id}`
      );
      return response.data.data;
    },

    /**
     * Create a new pause type
     */
    async create(data: CreatePauseTypeDTO): Promise<PauseType> {
      await getCsrfCookie();
      const response = await apiClient.post<PauseTypeResponse>(
        `${SETTINGS_BASE}/pause-types`,
        data
      );
      return response.data.data;
    },

    /**
     * Update an existing pause type
     */
    async update(id: number, data: UpdatePauseTypeDTO): Promise<PauseType> {
      await getCsrfCookie();
      const response = await apiClient.put<PauseTypeResponse>(
        `${SETTINGS_BASE}/pause-types/${id}`,
        data
      );
      return response.data.data;
    },

    /**
     * Delete a pause type
     */
    async delete(id: number): Promise<void> {
      await getCsrfCookie();
      await apiClient.delete(`${SETTINGS_BASE}/pause-types/${id}`);
    },

    /**
     * Toggle pause type active status
     */
    async toggleActive(id: number): Promise<PauseType> {
      await getCsrfCookie();
      const response = await apiClient.patch<PauseTypeResponse>(
        `${SETTINGS_BASE}/pause-types/${id}/toggle-active`
      );
      return response.data.data;
    },
  },

  /**
   * Payment Methods API
   */
  paymentMethods: {
    /**
     * Get all payment methods with their enabled status
     */
    async get(): Promise<PaymentMethodsResponse['data']> {
      const response = await apiClient.get<PaymentMethodsResponse>(
        `${SETTINGS_BASE}/payment-methods`
      );
      return response.data.data;
    },

    /**
     * Update payment method settings
     */
    async update(data: UpdatePaymentMethodsDTO): Promise<PaymentMethodsResponse['data']> {
      await getCsrfCookie();
      const response = await apiClient.put<PaymentMethodsResponse>(
        `${SETTINGS_BASE}/payment-methods`,
        data
      );
      return response.data.data;
    },
  },

  /**
   * Payment Gateway API
   */
  gateways: {
    /**
     * Get gateway settings
     */
    async get(): Promise<GatewaySettings> {
      const response = await apiClient.get<{ data: GatewaySettings }>(
        `${SETTINGS_BASE}/gateways`
      );
      return response.data.data;
    },

    /**
     * Update gateway settings
     */
    async update(data: UpdateGatewaySettingsDTO): Promise<GatewaySettings> {
      await getCsrfCookie();
      const response = await apiClient.put<{ data: GatewaySettings }>(
        `${SETTINGS_BASE}/gateways`,
        data
      );
      return response.data.data;
    },

    /**
     * Test gateway connection
     */
    async testConnection(
      gateway: 'stripe' | 'mollie',
      credentials?: {
        secret_key?: string;
        publishable_key?: string;
        api_key?: string;
      }
    ): Promise<GatewayTestResponse> {
      await getCsrfCookie();
      const response = await apiClient.post<GatewayTestResponse>(
        `${SETTINGS_BASE}/gateways/test`,
        { gateway, ...(credentials ? { credentials } : {}) }
      );
      return response.data;
    },
  },

  /**
   * Company Settings API
   */
  company: {
    /**
     * Get company settings
     */
    async get(): Promise<CompanySettings> {
      const response = await apiClient.get<{ data: CompanySettings }>(
        `${SETTINGS_BASE}/company`
      );
      return response.data.data;
    },

    /**
     * Update company settings
     */
    async update(data: Partial<CompanySettingsFormData>): Promise<CompanySettings> {
      await getCsrfCookie();
      const response = await apiClient.put<{ data: CompanySettings }>(
        `${SETTINGS_BASE}/company`,
        data
      );
      return response.data.data;
    },

    /**
     * Upload company file (logo or favicon)
     */
    async uploadFile(type: CompanyFileType, file: File): Promise<FileUploadResponse> {
      const formData = new FormData();
      formData.append('file', file);

      await getCsrfCookie();
      const response = await apiClient.post<FileUploadResponse>(
        `${SETTINGS_BASE}/company/${type}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    },

    /**
     * Delete company file (logo or favicon)
     */
    async deleteFile(type: CompanyFileType): Promise<{ message: string }> {
      await getCsrfCookie();
      const response = await apiClient.delete<{ message: string }>(
        `${SETTINGS_BASE}/company/${type}`
      );
      return response.data;
    },
  },
  /**
   * Email Settings API
   *
   * FU8 — split single test endpoint into two:
   *   - testConnection() = SMTP probe (HELO/EHLO/AUTH), no mail sent
   *   - sendTest()       = Mail::raw to recipient, rate-limited 5/hour/admin
   *
   * update() accepts optional probe flag — when true, server runs the probe
   * before persisting and returns 422 if it fails.
   */
  email: {
    async get(): Promise<EmailSettings> {
      const response = await apiClient.get<{ data: EmailSettings }>(
        `${SETTINGS_BASE}/email`
      );
      return response.data.data;
    },

    async update(
      data: UpdateEmailSettingsDTO,
      options?: { probe?: boolean }
    ): Promise<EmailSettings> {
      const url = options?.probe
        ? `${SETTINGS_BASE}/email?probe=true`
        : `${SETTINGS_BASE}/email`;
      await getCsrfCookie();
      const response = await apiClient.put<{ data: EmailSettings }>(url, data);
      return response.data.data;
    },

    async testConnection(): Promise<MailProbeResponse> {
      await getCsrfCookie();
      const response = await apiClient.post<MailProbeResponse>(
        `${SETTINGS_BASE}/email/test-connection`
      );
      return response.data;
    },

    async sendTest(payload: MailSendTestPayload = {}): Promise<MailSendTestResponse> {
      await getCsrfCookie();
      const response = await apiClient.post<MailSendTestResponse>(
        `${SETTINGS_BASE}/email/send-test`,
        payload
      );
      return response.data;
    },
  },

  /**
   * Ticket Settings API
   */
  tickets: {
    async get(): Promise<TicketSettings> {
      const response = await apiClient.get<{ data: TicketSettings }>(
        `${SETTINGS_BASE}/tickets`
      );
      return response.data.data;
    },

    async update(data: UpdateTicketSettingsDTO): Promise<TicketSettings> {
      await getCsrfCookie();
      const response = await apiClient.put<{ data: TicketSettings }>(
        `${SETTINGS_BASE}/tickets`,
        data
      );
      return response.data.data;
    },
  },

  /**
   * Contact Defaults Settings API
   */
  contactDefaults: {
    async get(): Promise<ContactDefaultsSettings> {
      const response = await apiClient.get<{ data: ContactDefaultsSettings }>(
        `${SETTINGS_BASE}/contact-defaults`
      );
      return response.data.data;
    },

    async update(data: UpdateContactDefaultsSettingsDTO): Promise<ContactDefaultsSettings> {
      await getCsrfCookie();
      const response = await apiClient.put<{ data: ContactDefaultsSettings }>(
        `${SETTINGS_BASE}/contact-defaults`,
        data
      );
      return response.data.data;
    },
  },

  /**
   * SLA Compliance Target Settings API (TODO-6-002)
   */
  slaTargets: {
    async get(): Promise<SlaComplianceSettings> {
      const response = await apiClient.get<{ data: SlaComplianceSettings }>(
        `${SETTINGS_BASE}/sla-targets`
      );
      return response.data.data;
    },

    async update(data: UpdateSlaComplianceSettingsDTO): Promise<SlaComplianceSettings> {
      await getCsrfCookie();
      const response = await apiClient.put<{ data: SlaComplianceSettings }>(
        `${SETTINGS_BASE}/sla-targets`,
        data
      );
      return response.data.data;
    },
  },

  /**
   * UI Behaviour Settings API (TODO-6-006 — legacy visibility toggle)
   */
  ui: {
    async get(): Promise<UiSettings> {
      const response = await apiClient.get<{ data: UiSettings }>(`${SETTINGS_BASE}/ui`);
      return response.data.data;
    },

    async update(data: UpdateUiSettingsDTO): Promise<UiSettings> {
      await getCsrfCookie();
      const response = await apiClient.put<{ data: UiSettings }>(`${SETTINGS_BASE}/ui`, data);
      return response.data.data;
    },
  },

  /**
   * Customer-platform Branding Settings API (Wave 2b) — colors, logo,
   * hours/offices and social media links. Only `social` has a dedicated
   * admin UI today; the rest pass through the same GET/PUT contract.
   */
  branding: {
    async get(): Promise<BrandingSettings> {
      const response = await apiClient.get<{ data: BrandingSettings }>(
        `${SETTINGS_BASE}/branding`
      );
      return response.data.data;
    },

    async update(data: UpdateBrandingSettingsDTO): Promise<BrandingSettings> {
      await getCsrfCookie();
      const response = await apiClient.put<{ data: BrandingSettings }>(
        `${SETTINGS_BASE}/branding`,
        data
      );
      return response.data.data;
    },
  },

  /**
   * Languages Settings API (P3 F9 — multi-language).
   */
  languages: {
    async get(): Promise<LanguagesSettings> {
      const response = await apiClient.get<{ data: LanguagesSettings }>(
        `${SETTINGS_BASE}/languages`
      );
      return response.data.data;
    },

    async update(data: UpdateLanguagesSettingsDTO): Promise<LanguagesSettings> {
      await getCsrfCookie();
      const response = await apiClient.put<{ data: LanguagesSettings }>(
        `${SETTINGS_BASE}/languages`,
        data
      );
      return response.data.data;
    },
  },

  /**
   * Documenso (E-Signature) Settings API
   */
  documenso: {
    /**
     * Get Documenso integration settings
     */
    async get(): Promise<DocumensoSettings> {
      const response = await apiClient.get<{ data: DocumensoSettings }>(
        `${SETTINGS_BASE}/documenso`
      );
      return response.data.data;
    },

    /**
     * Update Documenso integration settings
     */
    async update(data: UpdateDocumensoSettingsDTO): Promise<DocumensoSettings> {
      await getCsrfCookie();
      const response = await apiClient.put<{ data: DocumensoSettings }>(
        `${SETTINGS_BASE}/documenso`,
        data
      );
      return response.data.data;
    },

    /**
     * Test Documenso connection
     */
    async testConnection(
      credentials?: { base_url?: string; api_key?: string }
    ): Promise<DocumensoTestResponse> {
      await getCsrfCookie();
      const response = await apiClient.post<DocumensoTestResponse>(
        `${SETTINGS_BASE}/documenso/test`,
        credentials ?? {}
      );
      return response.data;
    },
  },

  /**
   * Billing Platform API
   * Note: These endpoints are at /api/admin/billing-platform, not under SETTINGS_BASE
   */
  billingPlatform: {
    /**
     * Get billing platform settings including available drivers
     */
    async get(): Promise<BillingPlatformSettings> {
      const response = await apiClient.get<{ data: BillingPlatformSettings }>(
        '/api/admin/billing-platform'
      );
      return response.data.data;
    },

    /**
     * Update the active billing platform.
     *
     * A reason is required (audit trail). Pass force=true to override the
     * "unsynced invoices" guard or to disable the platform in production.
     */
    async update(
      platform: string,
      reason: string,
      force = false,
    ): Promise<{
      message: string;
      connection_test?: BillingPlatformTestResponse;
      unsynced_count?: number;
      requires_force?: boolean;
    }> {
      await getCsrfCookie();
      const response = await apiClient.put<{
        message: string;
        connection_test?: BillingPlatformTestResponse;
        unsynced_count?: number;
        requires_force?: boolean;
      }>(
        '/api/admin/billing-platform',
        { platform, reason, force }
      );
      return response.data;
    },

    /**
     * Test the active billing platform connection
     */
    async testConnection(platform?: string): Promise<BillingPlatformTestResponse> {
      await getCsrfCookie();
      const response = await apiClient.post<BillingPlatformTestResponse>(
        '/api/admin/billing-platform/test-connection',
        platform ? { platform } : {}
      );
      return response.data;
    },

    /**
     * Refresh the list of available billing platform drivers
     */
    async refreshDrivers(): Promise<{ available_drivers: BillingPlatformSettings['available_drivers'] }> {
      await getCsrfCookie();
      const response = await apiClient.post<{ data: { available_drivers: BillingPlatformSettings['available_drivers'] } }>(
        '/api/admin/billing-platform/refresh-drivers'
      );
      return response.data.data;
    },
  },

  /**
   * Factura Directa-specific settings (invoice payment-method + bank mappings).
   */
  facturaDirecta: {
    async getInvoicePaymentMethodMapping(): Promise<FdInvoicePaymentMethodMappingResponse> {
      const response = await apiClient.get<{ data: FdInvoicePaymentMethodMappingResponse }>(
        '/api/admin/factura-directa/invoice-payment-method-mapping'
      );
      return response.data.data;
    },

    async saveInvoicePaymentMethodMapping(mapping: Record<string, string>): Promise<{ mapping: Record<string, string> }> {
      await getCsrfCookie();
      const response = await apiClient.put<{ data: { mapping: Record<string, string> } }>(
        '/api/admin/factura-directa/invoice-payment-method-mapping',
        { mapping }
      );
      return response.data.data;
    },

    async refreshPaymentMethods(): Promise<{ fd_payment_methods: FdPaymentMethodOption[] }> {
      await getCsrfCookie();
      const response = await apiClient.post<{ data: { fd_payment_methods: FdPaymentMethodOption[] } }>(
        '/api/admin/factura-directa/refresh-payment-methods'
      );
      return response.data.data;
    },

    async getBankMapping(): Promise<FdBankMappingResponse> {
      const response = await apiClient.get<{ data: FdBankMappingResponse }>(
        '/api/admin/factura-directa/bank-mapping'
      );
      return response.data.data;
    },

    async saveBankMapping(mapping: Record<string, string>): Promise<{ mapping: Record<string, string> }> {
      await getCsrfCookie();
      const response = await apiClient.put<{ data: { mapping: Record<string, string> } }>(
        '/api/admin/factura-directa/bank-mapping',
        { mapping }
      );
      return response.data.data;
    },

    async refreshBanks(): Promise<{ fd_banks: FdBank[] }> {
      await getCsrfCookie();
      const response = await apiClient.post<{ data: { fd_banks: FdBank[] } }>(
        '/api/admin/factura-directa/refresh-banks'
      );
      return response.data.data;
    },
  },

  /**
   * Factura Directa Credentials Settings API
   */
  facturaDirectaCredentials: {
    async get(): Promise<FacturaDirectaCredentialsSettings> {
      const response = await apiClient.get<{ data: FacturaDirectaCredentialsSettings }>(
        `${SETTINGS_BASE}/factura-directa`
      );
      return response.data.data;
    },

    async update(data: UpdateFacturaDirectaCredentialsDTO): Promise<FacturaDirectaCredentialsSettings> {
      await getCsrfCookie();
      const response = await apiClient.put<{ data: FacturaDirectaCredentialsSettings }>(
        `${SETTINGS_BASE}/factura-directa`,
        data
      );
      return response.data.data;
    },

    async testConnection(
      credentials?: { api_key?: string; company_id?: string }
    ): Promise<FacturaDirectaCredentialsTestResponse> {
      await getCsrfCookie();
      const response = await apiClient.post<FacturaDirectaCredentialsTestResponse>(
        `${SETTINGS_BASE}/factura-directa/test`,
        credentials ?? {}
      );
      return response.data;
    },
  },

  /**
   * Anthropic AI Provider Settings API
   */
  anthropic: {
    async get(): Promise<AnthropicSettings> {
      const response = await apiClient.get<{ data: AnthropicSettings }>(
        `${SETTINGS_BASE}/anthropic`
      );
      return response.data.data;
    },

    async update(data: UpdateAnthropicSettingsDTO): Promise<AnthropicSettings> {
      await getCsrfCookie();
      const response = await apiClient.put<{ data: AnthropicSettings }>(
        `${SETTINGS_BASE}/anthropic`,
        data
      );
      return response.data.data;
    },

    async testConnection(
      credentials?: { api_key?: string; model?: string }
    ): Promise<AnthropicTestResponse> {
      await getCsrfCookie();
      const response = await apiClient.post<AnthropicTestResponse>(
        `${SETTINGS_BASE}/anthropic/test`,
        credentials ?? {}
      );
      return response.data;
    },
  },
};

export interface FdChannel {
  key: string;
  label: string;
}

export interface FdPaymentMethodOption {
  uuid: string;
  title: string;
  subtype: string;
  contact: string | null;
}

export interface FdInvoicePaymentMethodMappingResponse {
  local_channels: FdChannel[];
  fd_payment_methods: FdPaymentMethodOption[];
  mapping: Record<string, string>;
}

export interface FdBank {
  uuid: string;
  name: string;
  iban: string | null;
  account: string;
  bank_code: string | null;
  subtype: string;
}

export interface FdBankChannel {
  key: string;
  label: string;
  description?: string;
}

export interface FdBankMappingResponse {
  fd_banks: FdBank[];
  local_channels: FdBankChannel[];
  mapping: Record<string, string>;
}

export default settingsApi;

// ============================================================================
// VF-A — Verifactu tax-mapping settings (operator-editable, plan D-3 gated)
// ============================================================================

export interface VerifactuExemptionCause {
  code: string;
  fd_tax_id: string;
  calificacion_operacion?: string | null;
  operacion_exenta?: string | null;
  clave_regimen?: string | null;
  description?: string | null;
  description_es?: string | null;
}

export interface VerifactuTaxMappingSettings {
  exemption_causes: VerifactuExemptionCause[];
  fd_tax_id_export: string;
  fd_tax_id_reverse_charge: string;
  fd_tax_id_exempt_default: string;
  default_clave_regimen: string;
  catalogs: {
    calificacion_operacion: string[];
    operacion_exenta: string[];
    clave_regimen: string[];
  };
}

export type UpdateVerifactuTaxMappingDTO = Partial<
  Omit<VerifactuTaxMappingSettings, 'catalogs'>
>;

export const getVerifactuTaxMapping = async (): Promise<VerifactuTaxMappingSettings> => {
  const response = await apiClient.get<{ data: VerifactuTaxMappingSettings }>(
    '/api/admin/settings/verifactu'
  );
  return response.data.data;
};

export const updateVerifactuTaxMapping = async (
  data: UpdateVerifactuTaxMappingDTO
): Promise<VerifactuTaxMappingSettings> => {
  await getCsrfCookie();
  const response = await apiClient.put<{ data: VerifactuTaxMappingSettings }>(
    '/api/admin/settings/verifactu',
    data
  );
  return response.data.data;
};
